// Angular modules.
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
// Third-party modules.
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
// Zenklub interfaces.
import {
	AvailabilityEmitter,
	IAvailability,
	IAvailabilityApi,
} from 'app/interfaces/availability.interface';
import { ApiErrorResponse } from 'app/interfaces/error.interface';
import { IUserPreferences } from 'app/interfaces/userpreferences.interface';
// Zenklub services.
import { AvailabilitiesService } from 'app/services/availabilities.service';
import { DateTimeService } from 'app/services/datetime.service';
import { ErrorService } from 'app/services/error.service';
import { UserService } from 'app/services/user.service';
import { ReplaySubject } from 'rxjs/internal/ReplaySubject';

@Component({
	selector: 'app-list-availabilities',
	templateUrl: './list-availabilities.component.html',
	styleUrls: ['./list-availabilities.component.scss'],
})
export class ListAvailabilitiesComponent implements OnInit {
	// Inputs.
	@Input() currentTimezone: string;
	@Input() newPeriodDateFrom: Date;
	@Input() newPeriodDateTo: Date;
	@Input() resetProfileCompletion: ReplaySubject<boolean>;

	// Outputs.
	@Output() editMode: EventEmitter<AvailabilityEmitter> =
		new EventEmitter<AvailabilityEmitter>();

	// User's preferences.
	userPref: IUserPreferences;

	// Calendar data.
	calendarLabels: any;
	hoursList: string[] = this.dateTime.buildDailyHours(true);
	availableDays: number[] = [];
	isFilteredPeriod = false;
	daysOfWeek: string[] = [];

	// Availabilities data.
	availabilitiesShow: IAvailabilityApi[] = [];
	availabilityForEdit: IAvailability = null;
	isAddNew = false;
	minDate: Date = new Date();
	minDateString: string;
	maxDate: Date = new Date();
	maxDateString: string;
	totalShow = 0;

	// Translated strings.
	strToDateMustBeGreater: string;

	// Status.
	isLoading = false;

	// Constructor method.
	constructor(
		private availabilities: AvailabilitiesService,
		private dateTime: DateTimeService,
		private translate: TranslateService,
		protected notifications: ZkNotificationService,
		protected errorService: ErrorService,
		private userService: UserService
	) {}

	// On init.
	ngOnInit() {
		// Loader.
		this.isLoading = true;
		// Get user preferences.
		this.userPref = this.userService.getUserLocalPreferences();
		// Determine calendar modes and labels.
		this.translate
			.get([
				'CALENDAR.MODES.MONTH',
				'CALENDAR.MODES.WEEK',
				'CALENDAR.MODES.DAY',
				'CALENDAR.MONTHS',
				'CALENDAR.WEEKDAYS',
				'MESSAGES.TODATEMUSTBEGREATER',
			])
			.subscribe((res) => {
				// Calendar labels.
				const cMon: any = res['CALENDAR.MONTHS'];
				const cWee: any = res['CALENDAR.WEEKDAYS'];
				this.calendarLabels = {
					firstDayOfWeek: 0,
					dayNames: [
						cWee.SUN.SHORT,
						cWee.MON.SHORT,
						cWee.TUE.SHORT,
						cWee.WED.SHORT,
						cWee.THU.SHORT,
						cWee.FRI.SHORT,
						cWee.SAT.SHORT,
					],
					dayNamesShort: [
						cWee.SUN.ABB,
						cWee.MON.ABB,
						cWee.TUE.ABB,
						cWee.WED.ABB,
						cWee.THU.ABB,
						cWee.FRI.ABB,
						cWee.SAT.ABB,
					],
					monthNames: [
						cMon.JAN.FULL,
						cMon.FEB.FULL,
						cMon.MAR.FULL,
						cMon.APR.FULL,
						cMon.MAY.FULL,
						cMon.JUN.FULL,
						cMon.JUL.FULL,
						cMon.AUG.FULL,
						cMon.SEP.FULL,
						cMon.OCT.FULL,
						cMon.NOV.FULL,
						cMon.DEC.FULL,
					],
					monthNamesShort: [
						cMon.JAN.ABB,
						cMon.FEB.ABB,
						cMon.MAR.ABB,
						cMon.APR.ABB,
						cMon.MAY.ABB,
						cMon.JUN.ABB,
						cMon.JUL.ABB,
						cMon.AUG.ABB,
						cMon.SEP.ABB,
						cMon.OCT.ABB,
						cMon.NOV.ABB,
						cMon.DEC.ABB,
					],
				};
				// Strings.
				this.strToDateMustBeGreater = res['MESSAGES.TODATEMUSTBEGREATER'];
				// Get the names of days of week.
				this.daysOfWeek = [];
				for (let i = 0; i <= 6; i++) {
					const r = this.dateTime.getWeekdayName(i);
					this.daysOfWeek.push(r.ABB);
				}
				// Get availabilities.
				this.getAvailabilities();
			});
	}

	// Get availabilities list.
	getAvailabilities(): void {
		this.isLoading = true;
		// Get current availabilities.
		this.availabilities.getAll().subscribe((res) => {
			this.availabilitiesShow = res;
			this.totalShow = this.availabilitiesShow.length;
			this.isLoading = false;
		});
	}

	// Edit an hour interval.
	editItem(availability: IAvailability): void {
		this.availabilityForEdit = availability;
		this.isAddNew = false;
		this.editMode.emit({
			isEdit: true,
			item: availability,
			addNew: false,
		});
	}

	// Remove an hour interval.
	removeItem(availability: IAvailability): void {
		if (confirm(this.translate.instant('MESSAGES.CONFIRMREMOVEITEM'))) {
			this.availabilities.remove(availability.id).subscribe(
				() => {
					this.getAvailabilities();
					this.resetProfileCompletion.next(true);
				},
				(error: ApiErrorResponse) => {
					this.isLoading = false;
					this.notifications.showError(
						error.toString(),
						this.translate.instant['MESSAGES.SAVEDERROR']
					);
				}
			);
		}
	}

	// Add an hour interval.
	addHourInterval(availability: IAvailability): void {
		this.availabilityForEdit = availability;
		this.isAddNew = true;
		this.editMode.emit({
			isEdit: true,
			item: availability,
			addNew: true,
		});
	}

	// Add a new hour interval.
	addNew(): void {
		const dtFrom: Date = this.minDate;
		const dtTo: Date = this.maxDate;
		this.editMode.emit({
			isEdit: true,
			item: null,
			addNew: true,
			from: dtFrom,
			to: dtTo,
		});
	}
}
